import { NgModule, ModuleWithProviders, ErrorHandler } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FlexLayoutModule, MediaMarshaller } from 'ngx-flexible-layout';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { FileUploadModule } from 'ng2-file-upload';
import { MaterialModule } from 'src/app/material.module';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha';
import { AuthGuardService } from 'src/app/guards/auth-guard.service';
import { ProductionGuardService } from 'src/app/guards/production-guard.service';
import { StagingGuardService } from 'src/app/guards/staging-guard.service';
import { AWSService } from 'src/app/shared/services/aws.service';
import { GlobalErrorHandler } from 'src/app/global.error-config';
import { OrderByPipe } from 'src/app/shared/pipes/order-by.pipe';
import { currentOrUpcomingTeamsSummaryPipe } from 'src/app/shared/pipes/currentOrUpcomingTeamsSummary.pipe';
import { CountryNamePipe } from './pipes/country-name.pipe';
import { LocalDatePipe } from 'src/app/shared/pipes/local-date.pipe';
import { SearchModeService } from 'src/app/shared/services/search-mode.service';
import { FilterByPipe } from 'src/app/shared/pipes/filter-by.pipe';
import { SearchBarComponent } from 'src/app/shared/components/search-bar/search-bar.component';
import { ModalConfirmationComponent } from 'src/app/shared/components/modal-confirmation/modal-confirmation.component';
import { PreviewApplicationModalComponent } from 'src/app/shared/components/preview-application-modal/preview-application-modal.component';
import { DeclineReasonModalComponent } from 'src/app/shared/components/decline-reason-modal/decline-reason-modal.component';
import { SurveyComponent } from 'src/app/members/survey/survey.component';
import { SurveyIntroductionComponent } from 'src/app/members/survey/survey-introduction/survey-introduction.component';
import { SurveyRespondComponent } from 'src/app/members/survey/survey-respond/survey-respond.component';
import { SurveyConfirmationComponent } from 'src/app/members/survey/survey-confirmation/survey-confirmation.component';
import { ApplicationComponent } from 'src/app/members/application/application.component';
import { ValidationSummaryComponent } from 'src/app/members/validation-summary/validation-summary.component';
import { ApplicationPaymentComponent } from 'src/app/members/application-payment/application-payment.component';
import { ApplicationConfirmationComponent } from 'src/app/members/application/application-confirmation/application-confirmation.component';
import { DeletePersonModalComponent } from 'src/app/shared/components/delete-person-modal/delete-person-modal.component';
import { DeletePersonWorkspaceModalComponent } from './components/delete-person-workspace-modal/delete-person-workspace-modal.component';
import { DeleteWorkspaceModalComponent } from './components/delete-workspace-modal/delete-workspace-modal.component';
import { TagService } from 'src/app/shared/services/tag.service';
import { AvatarComponent } from 'src/app/shared/components/avatar/avatar.component';
import { CloudinaryUploadComponent } from 'src/app/shared/components/cloudinary-upload/cloudinary-upload.component';
import { CheckboxGroup } from 'src/app/shared/components/checkbox-group/checkbox-group.component';
import { DocumentUploadComponent } from './components/document-upload/document-upload.component';
import { TeamHeaderComponent } from 'src/app/teams/team-header/team-header.component';
import { TeamsNavComponent } from 'src/app/teams/teams-nav/teams-nav.component';
import { PositionDetailsComponent } from 'src/app/teams/position-details/position-details.component';
import { TeamAddComponent } from 'src/app/teams/team-add/team-add.component';
import { TeamAddDetailsComponent } from 'src/app/teams/team-add/team-add-details.component';
import { PercentageContainerComponent } from 'src/app/home/percentage-container/percentage-container.component';
import { AttributeChartComponent } from 'src/app/home/attribute-chart/attribute-chart.component';
import { NavigateDirective } from 'src/app/shared/directives/navigate.directive';
import { AutoTextResizeDirective } from 'src/app/shared/directives/auto-text-resize/auto-text-resize.directive';
import { DebounceKeyupDirective } from 'src/app/shared/directives/debounce/debounce-keyup.directive';
import { DebounceInputDirective } from 'src/app/shared/directives/debounce/debounce-input.directive';
import { ThrottleClickDirective } from 'src/app/shared/directives/throttle/throttle-click.directive';
import { MembersService } from 'src/app/shared/services/members.service';
import { ExportService } from 'src/app/shared/services/export.service';
import { EmailMessageService } from 'src/app/shared/services/email-message.service';
import { SidenavContainerComponent } from 'src/app/shared/components/sidenav-container/sidenav-container.component';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { TeamHistoricalAddComponent } from 'src/app/teams/team-historical/team-historical-add.component';
import { AttributesEditorComponent } from './components/attributes-editor/attributes-editor.component';
import { AddressComponent } from './components/address/address.component';
import { AddressEditorComponent } from './components/address-editor/address-editor.component';
import { DialogComponent } from './components/dialog/dialog.component';
import { ReadOnlyAddressComponent } from './components/read-only-address/read-only-address.component';
import { MessageContentComponent } from 'src/app/admin/admin-home/message-center/content/message-center-content.component';
import { EmailCustomizationResolver } from 'src/app/shared/resolvers/email-customization.resolver';
import { AutoFocusDirective } from 'src/app/shared/directives/auto-focus.directive';
import { SurveyService } from './services/survey.service';
import { TeamFormService } from './services/team-form.service';
import { EditableLabeledSimpleTextComponent } from './editable-labeled-simple-text/editable-labeled-simple-text.component';
import { StarRatingComponent } from './components/star-rating/star-rating.component';
import { NumberRangeEditorComponent } from './components/number-range-editor/number-range-editor.component';
import { ItemPickerComponent } from './components/item-picker/item-picker.component';
import { FilterPipe } from './components/item-picker/filter.pipe';
import { GetPreviewPipe } from './pipes/get-preview.pipe';
import { EmbedVideoComponent } from './components/embed-video/embed-video.component';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { Base64UrlEncodedPipe } from './pipes/base64-url-encoded';
import { SendApplicationComponent } from './components/send-application/send-application.component';
import { MultiselectComponent } from './components/multiselect/multiselect.component';
import { DateDifferencePipe } from './pipes/date-difference.pipe';
import { CenterMatmenuDirective } from './directives/center-matmenu.directive';
import { CustomFieldsInlineEditorComponent } from './components/custom-fields-inline-editor/custom-fields-inline-editor.component';
import { CustomFieldInlineEditorComponent } from 'src/app/shared/components/custom-fields-inline-editor/custom-field-inline-editor/custom-field-inline-editor.component';
import { ProfileCustomFieldComponent } from './components/profile-custom-field/profile-custom-field.component';
import { ProfileCustomFieldEditorComponent } from 'src/app/shared/components/profile-custom-field/profile-custom-field-editor/profile-custom-field-editor.component';
import { ODataFilterComponent } from './components/odata-filter/odata-filter.component';
import { ODataFilterItemComponent } from './components/odata-filter/odata-filter-item/odata-filter-item.component';
import { CustomViewMenuComponent } from './components/custom-view-menu/custom-view-menu.component';
import { TenantPlanErrorModalComponent } from './components/tenant-plan-error-modal/tenant-plan-error-modal.component';
import { CustomViewSidebarComponent } from './components/custom-view-sidebar/custom-view-sidebar.component';
import { PlanUpgradeBoxComponent } from './components/plan-upgrade-box/plan-upgrade-box.component';
import { BrowserReloadDialogComponent } from './components/browser-reload-dialog/browser-reload-dialog.component';
import { ManageColumnsSidebarComponent } from './components/manage-columns-sidebar/manage-columns-sidebar.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ManageColumnsComponent } from './components/manage-columns-sidebar/manage-columns/manage-columns.component';
import { SkillComponent } from './components/skill/skill.component';
import { PersonCardComponent } from './components/person-card/person-card.component';
import { QuickODataFilterComponent } from './components/quick-odata-filter/quick-odata-filter.component';
import { QuickODataFilterItemComponent } from './components//quick-odata-filter/quick-odata-filter-item/quick-odata-filter-item.component';
import { PreviewTemplateMessageComponent } from './components/preview-template-message/preview-template-message.component';
import { ManageQiuckOdataFiltersComponent } from './components/quick-odata-filter/manage-qiuck-odata-filters/manage-qiuck-odata-filters.component';
import { NgChartsModule } from 'ng2-charts';
import { ChartCanvasComponent } from './components/chart-canvas/chart-canvas.component';
import { ExpiresInPipe } from './pipes/expires-in.pipe';
import { ExportConfirmationComponent } from './components/modal-confirmation/export-confirmation/export-confirmation.component';
import { CancelReminderModalComponent } from './components/cancel-reminder-modal/cancel-reminder-modal.component';
import { StarRatingAttributeComponent } from './components/star-rating-attribute/star-rating-attribute.component';
import { EmailReminderService } from './services/email-reminder.service';
import { AceEditorComponent } from './components/ace-editor/ace-editor.component';
import { RichTextEditorComponent } from './components/rich-text-editor/rich-text-editor.component';
import { DateRangeComponent } from './components/daterange/daterange.component';
import { EditableLabeledRichTextComponent } from './components/editable-labeled-rich-text/editable-labeled-rich-text.component';
import { SendEmailComponent } from './components/send-email/send-email.component';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { EmailTemplateTileComponent } from './components/email-template-tile/email-template-tile.component';
import { CustomDocumentViewerComponent } from './components/custom-document-viewer/custom-document-viewer.component';
import { SeatStatusOverrideComponent } from './components/seat-status-override/seat-status-override.component';
import { CustomDocumentUploaderComponent } from './components/custom-document-uploader/custom-document-uploader.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { ComposeEmailComponent } from './compose-email/compose-email.component';
import { DataImportErrorModalComponent } from './components/data-import-error-modal/data-import-error-modal.component';
import { CustomFieldEditorModalComponent } from './components/custom-field-editor-modal/custom-field-editor-modal.component';
import { WorkspaceNamePipe } from './pipes/workspace-name.pipe';
import { EvaluationTeamRatingsPipe } from './pipes/evaluation-team-ratings.pipe';
import { EvaluationAggregatePipe } from './pipes/evaluation-aggregate.pipe';
import { PersonEvaluationDetailsComponent } from './components/person-evaluation-details/person-evaluation-details.component';
import { EnumeratedAggregateItemComponent } from './components/person-evaluation-details/enumerated-aggregate-item/enumerated-aggregate-item.component';
import { StarRatingAggregateItemComponent } from './components/person-evaluation-details/star-rating-aggregate-item/star-rating-aggregate-item.component';
import { PersonFormDetailsComponent } from './components/person-form-details/person-form-details.component';
import { CertificationDetailModalComponent } from './components/certification-detail-modal/certification-detail-modal.component';
import { FieldDataHistoricalModalComponent } from './components/field-data-historical-modal/field-data-historical-modal.component';
import { CustomFieldsDescription } from './components/custom-fields-description/custom-fields-description.component';
import { EditExternalSystemIdModalComponent } from './components/edit-external-system-id-modal/edit-external-system-id-modal.component';
import { HistoryService } from './services/history.service';
import { ComplexFieldViewerComponent } from './components/complex-field-viewer/complex-field-viewer.component';
import { ComplexFieldEditorComponent } from './components/complex-field-editor/complex-field-editor.component';
import { TargetQuestionNamePipe } from './pipes/target-question-name.pipe';
import { OpportunityBoardService } from './services/opportunity-board.service';
import { InactiveLinkComponent } from './components/inactive-link/inactive-link.component';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { AllowModuleDirective } from './directives/allow-module.directive';
import { CustomFieldTypeIconPipe } from './pipes/custom-field-type-icon.pipe';
import { CustomFieldTitlePipe } from './pipes/custom-field-title.pipe';
import { AutomationConditionBuilderComponent } from './components/automation-condition-builder/automation-condition-builder.component';
import { AutomationConditionBuilderItemComponent } from './components/automation-condition-builder/automation-condition-builder-item/automation-condition-builder-item.component';
import { DeleteDialogComponent } from './delete-dialog/delete-dialog.component';
import { DialogDirective } from './components/dialog/dialog.directive';
import { DeleteDialogDirective } from './delete-dialog/delete-dialog.directive';
import { CustomFieldValueByIdPipe } from './pipes/custom-field-value-by-id.pipe copy';
import { ScenarioNotesSidebarComponent } from './components/scenario-notes-sidebar/scenario-notes-sidebar.component';
import { RequiredAriaDirective } from './directives/required-aria.directive';
import { FeeAdjustmentConditionPipe } from './pipes/fee-adjustment-condition.pipe';
import { RequirementFeeAdjustmentTableComponent } from './components/requirement-fee-adjustment-table/requirement-fee-adjustment-table.component';
import { SpaceCapCasePipe } from './pipes/spaceCapCase.pipe';
import { YesNoPipe } from './pipes/yesno-piipe';
import {
  MonthDayHeader,
  MonthDayPickerComponent,
} from './components/month-day-picker/month-day-picker.component';
import { AutoAddAutomaionNamePipe } from './pipes/auto-add-automation-name.pipe';
import { ApplicationConfirmationPaymentComponent } from '../members/application/application-confirmation/application-confirmation-payments/application-confirmation-payment.component';
import { CustomFieldUsageReportComponent } from './components/custom-field-usage-report/custom-field-usage-report.component';
import { FormPickerComponent } from './components/form-picker/form-picker.component';
import { TimeZoneComponent } from './timezone/timezone.component';
import { InvoiceDisplayPipe } from './pipes/invoice-display.pipe';
import { FieldDisplayPipe } from './pipes/field-display.pipe';
import { EditPhoneComponent } from './components/phone/phone.component';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { DisplayDataComponent } from './components/dispay-data/display-data.component';
import { CertificationSnapshotComponent } from './components/dispay-data/snap-shot/certification-snapshot.component';
import { CredentialSnapshotComponent } from './components/dispay-data/snap-shot/credential-snapshot.component';
import { ComplexFieldSnapshotComponent } from './components/dispay-data/snap-shot/complex-field-snapshot.component';
import { EvidenceSnapshotComponent } from './components/dispay-data/snap-shot/evidence-snapshot.component';
import { ScenarioHistorySidebarComponent } from './components/scenario-history-sidebar/scenario-history-sidebar.component';
import { CustomCircleProgressBarComponent } from './components/custom-circle-progress-bar/custom-circle-progress-bar.component';
import { PersonCredentialApplicationReportComponent } from './components/person-credential-application-report/person-credential-application-report.component';

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    MaterialModule,
    FlexLayoutModule,
    FormsModule,
    ReactiveFormsModule,
    FileUploadModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    DragDropModule,
    NgChartsModule,
    CKEditorModule,
    NgScrollbarModule,
    NgxMatIntlTelInputComponent,
  ],
  exports: [
    CustomFieldsInlineEditorComponent,
    CustomFieldInlineEditorComponent,
    ProfileCustomFieldComponent,
    ProfileCustomFieldEditorComponent,
    SendApplicationComponent,
    AttributesEditorComponent,
    StarRatingComponent,
    SearchBarComponent,
    OrderByPipe,
    currentOrUpcomingTeamsSummaryPipe,
    CountryNamePipe,
    AvatarComponent,
    MaterialModule,
    FlexLayoutModule,
    CloudinaryUploadComponent,
    CheckboxGroup,
    DocumentUploadComponent,
    FormsModule,
    LocalDatePipe,
    ReactiveFormsModule,
    TeamHeaderComponent,
    TeamsNavComponent,
    FilterByPipe,
    FieldDisplayPipe,
    PositionDetailsComponent,
    SidenavContainerComponent,
    NavigateDirective,
    RequiredAriaDirective,
    AutoTextResizeDirective,
    DebounceKeyupDirective,
    DebounceInputDirective,
    ThrottleClickDirective,
    PercentageContainerComponent,
    AttributeChartComponent,
    AddressComponent,
    AddressEditorComponent,
    DialogDirective,
    DialogComponent,
    DeleteDialogDirective,
    DeleteDialogComponent,
    ReadOnlyAddressComponent,
    StarRatingComponent,
    RecaptchaModule,
    RecaptchaFormsModule,
    EditableLabeledSimpleTextComponent,
    NumberRangeEditorComponent,
    ItemPickerComponent,
    FilterPipe,
    GetPreviewPipe,
    EmbedVideoComponent,
    SafeUrlPipe,
    SafeHtmlPipe,
    Base64UrlEncodedPipe,
    YesNoPipe,
    MultiselectComponent,
    DateDifferencePipe,
    CenterMatmenuDirective,
    ODataFilterComponent,
    CustomViewMenuComponent,
    CustomViewSidebarComponent,
    PlanUpgradeBoxComponent,
    BrowserReloadDialogComponent,
    ManageColumnsSidebarComponent,
    ManageColumnsComponent,
    SkillComponent,
    PersonCardComponent,
    ODataFilterItemComponent,
    QuickODataFilterComponent,
    QuickODataFilterItemComponent,
    PreviewTemplateMessageComponent,
    ManageQiuckOdataFiltersComponent,
    NgChartsModule,
    ChartCanvasComponent,
    ExpiresInPipe,
    CancelReminderModalComponent,
    StarRatingAttributeComponent,
    AceEditorComponent,
    RichTextEditorComponent,
    DateRangeComponent,
    EditableLabeledRichTextComponent,
    SendEmailComponent,
    DocumentUploadComponent,
    EmailTemplateTileComponent,
    CustomDocumentUploaderComponent,
    SeatStatusOverrideComponent,
    PaginatorComponent,
    ComposeEmailComponent,
    WorkspaceNamePipe,
    EvaluationTeamRatingsPipe,
    EvaluationAggregatePipe,
    PersonEvaluationDetailsComponent,
    StarRatingAggregateItemComponent,
    EnumeratedAggregateItemComponent,
    PersonFormDetailsComponent,
    EditExternalSystemIdModalComponent,
    TargetQuestionNamePipe,
    ComplexFieldViewerComponent,
    InactiveLinkComponent,
    NgScrollbarModule,
    AllowModuleDirective,
    CustomFieldTypeIconPipe,
    CustomFieldTitlePipe,
    AutomationConditionBuilderComponent,
    CustomFieldValueByIdPipe,
    ScenarioNotesSidebarComponent,
    ScenarioHistorySidebarComponent,
    FeeAdjustmentConditionPipe,
    RequirementFeeAdjustmentTableComponent,
    SpaceCapCasePipe,
    MonthDayPickerComponent,
    MonthDayHeader,
    ApplicationConfirmationPaymentComponent,
    AutoAddAutomaionNamePipe,
    CustomFieldUsageReportComponent,
    FormPickerComponent,
    TimeZoneComponent,
    InvoiceDisplayPipe,
    EditPhoneComponent,
    NgxMatIntlTelInputComponent,
    DisplayDataComponent,
    CertificationSnapshotComponent,
    CredentialSnapshotComponent,
    ComplexFieldSnapshotComponent,
    EvidenceSnapshotComponent,
    CustomCircleProgressBarComponent,
    PersonCredentialApplicationReportComponent,
  ],
  declarations: [
    CustomFieldInlineEditorComponent,
    CustomFieldsInlineEditorComponent,
    ProfileCustomFieldComponent,
    ProfileCustomFieldEditorComponent,
    SendApplicationComponent,
    AttributesEditorComponent,
    TeamHeaderComponent,
    SearchBarComponent,
    AvatarComponent,
    CloudinaryUploadComponent,
    CheckboxGroup,
    DocumentUploadComponent,
    TeamsNavComponent,
    OrderByPipe,
    currentOrUpcomingTeamsSummaryPipe,
    CountryNamePipe,
    LocalDatePipe,
    FilterByPipe,
    FieldDisplayPipe,
    PositionDetailsComponent,
    ModalConfirmationComponent,
    PreviewApplicationModalComponent,
    DeclineReasonModalComponent,
    ApplicationComponent,
    ValidationSummaryComponent,
    ApplicationPaymentComponent,
    ApplicationConfirmationComponent,
    ApplicationConfirmationPaymentComponent,
    DeletePersonModalComponent,
    DeletePersonWorkspaceModalComponent,
    DeleteWorkspaceModalComponent,
    TeamAddComponent,
    TeamAddDetailsComponent,
    RequiredAriaDirective,
    SidenavContainerComponent,
    TeamHistoricalAddComponent,
    NavigateDirective,
    AutoFocusDirective,
    AutoTextResizeDirective,
    DebounceKeyupDirective,
    DebounceInputDirective,
    ThrottleClickDirective,
    StarRatingComponent,
    PercentageContainerComponent,
    AttributeChartComponent,
    AddressComponent,
    AddressEditorComponent,
    DialogDirective,
    DialogComponent,
    DeleteDialogDirective,
    DeleteDialogComponent,
    ReadOnlyAddressComponent,
    MessageContentComponent,
    EditableLabeledSimpleTextComponent,
    NumberRangeEditorComponent,
    ItemPickerComponent,
    FilterPipe,
    GetPreviewPipe,
    EmbedVideoComponent,
    SafeUrlPipe,
    YesNoPipe,
    SafeHtmlPipe,
    Base64UrlEncodedPipe,
    MultiselectComponent,
    DateDifferencePipe,
    CenterMatmenuDirective,
    ODataFilterComponent,
    ODataFilterItemComponent,
    CustomViewMenuComponent,
    TenantPlanErrorModalComponent,
    CustomViewSidebarComponent,
    PlanUpgradeBoxComponent,
    BrowserReloadDialogComponent,
    ManageColumnsSidebarComponent,
    ManageColumnsComponent,
    SkillComponent,
    PersonCardComponent,
    QuickODataFilterComponent,
    QuickODataFilterItemComponent,
    PreviewTemplateMessageComponent,
    ManageQiuckOdataFiltersComponent,
    ChartCanvasComponent,
    ExpiresInPipe,
    ExportConfirmationComponent,
    CancelReminderModalComponent,
    StarRatingAttributeComponent,
    AceEditorComponent,
    RichTextEditorComponent,
    DateRangeComponent,
    EditableLabeledRichTextComponent,
    SendEmailComponent,
    EmailTemplateTileComponent,
    DocumentUploadComponent,
    CustomDocumentUploaderComponent,
    SeatStatusOverrideComponent,
    CustomDocumentViewerComponent,
    PaginatorComponent,
    ComposeEmailComponent,
    DataImportErrorModalComponent,
    CustomFieldEditorModalComponent,
    WorkspaceNamePipe,
    EvaluationTeamRatingsPipe,
    EvaluationAggregatePipe,
    PersonEvaluationDetailsComponent,
    StarRatingAggregateItemComponent,
    EnumeratedAggregateItemComponent,
    PersonFormDetailsComponent,
    CertificationDetailModalComponent,
    FieldDataHistoricalModalComponent,
    CustomFieldsDescription,
    SurveyComponent,
    SurveyIntroductionComponent,
    SurveyRespondComponent,
    SurveyConfirmationComponent,
    EditExternalSystemIdModalComponent,
    ComplexFieldViewerComponent,
    ComplexFieldEditorComponent,
    TargetQuestionNamePipe,
    InactiveLinkComponent,
    AllowModuleDirective,
    CustomFieldTypeIconPipe,
    CustomFieldTitlePipe,
    AutomationConditionBuilderComponent,
    AutomationConditionBuilderItemComponent,
    CustomFieldValueByIdPipe,
    ScenarioNotesSidebarComponent,
    ScenarioHistorySidebarComponent,
    FeeAdjustmentConditionPipe,
    RequirementFeeAdjustmentTableComponent,
    SpaceCapCasePipe,
    MonthDayPickerComponent,
    MonthDayHeader,
    AutoAddAutomaionNamePipe,
    CustomFieldUsageReportComponent,
    FormPickerComponent,
    TimeZoneComponent,
    InvoiceDisplayPipe,
    EditPhoneComponent,
    DisplayDataComponent,
    CertificationSnapshotComponent,
    CredentialSnapshotComponent,
    ComplexFieldSnapshotComponent,
    EvidenceSnapshotComponent,
    CustomCircleProgressBarComponent,
    PersonCredentialApplicationReportComponent,
  ],
})
export class SharedModule {
  lastValue;
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        SearchModeService,
        TagService,
        AuthGuardService,
        ProductionGuardService,
        StagingGuardService,
        AWSService,
        {
          provide: ErrorHandler,
          useClass: GlobalErrorHandler,
        },
        LookupService,
        MembersService,
        ExportService,
        EmailMessageService,
        EmailCustomizationResolver,
        SurveyService,
        TeamFormService,
        EmailReminderService,
        DatePipe,
        HistoryService,
        OpportunityBoardService,
      ],
    };
  }

  public constructor(m: MediaMarshaller) {
    // hack until resolve: https://github.com/angular/flex-layout/issues/1201
    // @ts-ignore
    m.subject.subscribe(() => {
      // @ts-ignore
      if (m.activatedBreakpoints.filter((b) => b.alias === 'print').length === 0) {
        // @ts-ignore
        this.lastValue = [...m.activatedBreakpoints];
      } else {
        // @ts-ignore
        m.activatedBreakpoints = [...this.lastValue];
        // @ts-ignore
        m.hook.collectActivations = () => {};
        // @ts-ignore
        m.hook.deactivations = [...this.lastValue];
      }
    });
  }
}
